import type {
  INeoProjectService,
  NeoProjectError,
} from "../ports/driver/neoProjectService";
import type {
  INeoProjectRepository,
  NeoProjectFilePayload,
  NeoProjectFilePatchPayload,
  NeoProjectFileRoleRequest,
  NeoProjectInstructionPayload,
  NeoProjectPayload,
  NeoProjectUpdatePayload,
  NeoTaskPayload,
  NeoTemplateClonePayload,
  NeoWorkflowDocumentListOptions,
} from "../ports/driven/neoProjectRepository";

const toError = (error: unknown): NeoProjectError => ({
  message: error instanceof Error ? error.message : "Something went wrong.",
});

export const NeoProjectServiceImplFactory = ({
  neoProjectRepository,
}: {
  neoProjectRepository: INeoProjectRepository;
}): INeoProjectService => {
  const repo = neoProjectRepository;

  // ── Projects ──────────────────────────────────────────────────────────
  const listProjects: INeoProjectService["listProjects"] = async () => {
    try {
      const resp = await repo.listProjectsAPI();
      return { success: true, data: resp?.items ?? [] };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const createProject = async (payload: NeoProjectPayload) => {
    try {
      const data = await repo.createProjectAPI(payload);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  // ── Templates ─────────────────────────────────────────────────────────
  const listTemplates = async () => {
    try {
      const resp = await repo.listTemplatesAPI();
      return { success: true, data: resp?.items ?? [] };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const listPromptSuggestions = async () => {
    try {
      const resp = await repo.listPromptSuggestionsAPI();
      return { success: true, data: resp?.items ?? [] };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const cloneTemplate = async (
    templateId: number,
    payload: NeoTemplateClonePayload
  ) => {
    try {
      const data = await repo.cloneTemplateAPI(templateId, payload);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const updateProject = async (
    projectId: number,
    payload: NeoProjectUpdatePayload
  ) => {
    try {
      const data = await repo.updateProjectAPI(projectId, payload);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const deleteProject = async (projectId: number) => {
    try {
      const data = await repo.deleteProjectAPI(projectId);
      return { success: true, data: data ?? null };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const getProjectShare = async (projectId: number) => {
    try {
      const data = await repo.getProjectShareAPI(projectId);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const setProjectShareClinics = async (
    projectId: number,
    clinicIds: number[]
  ) => {
    try {
      const data = await repo.setProjectShareClinicsAPI(projectId, clinicIds);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const getAiCareSnfSettings = async (clinicId: number) => {
    try {
      const data = await repo.getAiCareSnfSettingsAPI(clinicId);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const setAiCareSnfEnabled = async (clinicId: number, enabled: boolean) => {
    try {
      const data = await repo.setAiCareSnfEnabledAPI(clinicId, enabled);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const getProjectOwners = async (projectId: number) => {
    try {
      const data = await repo.getProjectOwnersAPI(projectId);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const setProjectOwners = async (
    projectId: number,
    emails: string[],
    ownerTiers?: Record<string, "primary" | "secondary">
  ) => {
    try {
      const data = await repo.setProjectOwnersAPI(
        projectId,
        emails,
        ownerTiers
      );
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  // ── Activity ──────────────────────────────────────────────────────────
  const listProjectActivity = async (projectId: number) => {
    try {
      const resp = await repo.listProjectActivityAPI(projectId);
      return { success: true, data: resp?.items ?? [] };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  // ── Agents ────────────────────────────────────────────────────────────
  const listProjectAgents = async (projectId: number) => {
    try {
      const resp = await repo.listProjectAgentsAPI(projectId);
      return { success: true, data: resp?.items ?? [] };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  // ── Files ─────────────────────────────────────────────────────────────
  const listProjectFiles = async (projectId: number) => {
    try {
      const resp = await repo.listProjectFilesAPI(projectId);
      return { success: true, data: resp?.items ?? [] };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const listProjectWorkflowDocuments = async (
    projectId: number,
    options?: NeoWorkflowDocumentListOptions
  ) => {
    try {
      const resp = await repo.listProjectWorkflowDocumentsAPI(
        projectId,
        options
      );
      return {
        success: true,
        data: {
          items: resp?.items ?? [],
          total: resp?.total ?? 0,
          limit: resp?.limit ?? options?.limit ?? 25,
          offset: resp?.offset ?? options?.offset ?? 0,
        },
      };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const listAgentFiles = async (projectId: number, agentId: number) => {
    try {
      const resp = await repo.listAgentFilesAPI(projectId, agentId);
      return { success: true, data: resp?.items ?? [] };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const addProjectFile = async (
    projectId: number,
    payload: NeoProjectFilePayload
  ) => {
    try {
      const data = await repo.addProjectFileAPI(projectId, payload);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const addAgentFile = async (
    projectId: number,
    agentId: number,
    file: File,
    description?: string,
    role?: NeoProjectFileRoleRequest
  ) => {
    try {
      const data = await repo.addAgentFileAPI(
        projectId,
        agentId,
        file,
        description,
        role
      );
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const patchAgentFile = async (
    projectId: number,
    agentId: number,
    fileId: number,
    payload: NeoProjectFilePatchPayload
  ) => {
    try {
      const data = await repo.patchAgentFileAPI(
        projectId,
        agentId,
        fileId,
        payload
      );
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const deleteProjectFile = async (projectId: number, fileId: number) => {
    try {
      await repo.deleteProjectFileAPI(projectId, fileId);
      return { success: true, data: null };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const deleteAgentFile = async (
    projectId: number,
    agentId: number,
    fileId: number
  ) => {
    try {
      await repo.deleteAgentFileAPI(projectId, agentId, fileId);
      return { success: true, data: null };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  // ── Instructions ──────────────────────────────────────────────────────
  const listProjectInstructions = async (projectId: number) => {
    try {
      const resp = await repo.listProjectInstructionsAPI(projectId);
      return { success: true, data: resp?.items ?? [] };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const listAgentInstructions = async (projectId: number, agentId: number) => {
    try {
      const resp = await repo.listAgentInstructionsAPI(projectId, agentId);
      return { success: true, data: resp?.items ?? [] };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const createAgentInstruction = async (
    projectId: number,
    agentId: number,
    payload: NeoProjectInstructionPayload
  ) => {
    try {
      const data = await repo.createAgentInstructionAPI(
        projectId,
        agentId,
        payload
      );
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const updateAgentInstruction = async (
    projectId: number,
    agentId: number,
    instructionId: number,
    payload: NeoProjectInstructionPayload
  ) => {
    try {
      const data = await repo.updateAgentInstructionAPI(
        projectId,
        agentId,
        instructionId,
        payload
      );
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const deleteAgentInstruction = async (
    projectId: number,
    agentId: number,
    instructionId: number
  ) => {
    try {
      await repo.deleteAgentInstructionAPI(projectId, agentId, instructionId);
      return { success: true, data: null };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  // ── Memories ──────────────────────────────────────────────────────────
  const listProjectMemories = async (projectId: number) => {
    try {
      const resp = await repo.listProjectMemoriesAPI(projectId);
      return { success: true, data: resp?.items ?? [] };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const listAgentMemories = async (projectId: number, agentId: number) => {
    try {
      const resp = await repo.listAgentMemoriesAPI(projectId, agentId);
      return { success: true, data: resp?.items ?? [] };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const deleteAgentMemory = async (
    projectId: number,
    agentId: number,
    memoryId: number
  ) => {
    try {
      await repo.deleteAgentMemoryAPI(projectId, agentId, memoryId);
      return { success: true, data: null };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  // ── Tasks ─────────────────────────────────────────────────────────────
  const listProjectTasks = async (projectId: number) => {
    try {
      const resp = await repo.listProjectTasksAPI(projectId);
      return { success: true, data: resp?.items ?? [] };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const createProjectTask = async (
    projectId: number,
    payload: NeoTaskPayload
  ) => {
    try {
      const data = await repo.createProjectTaskAPI(projectId, payload);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const updateProjectTask = async (
    projectId: number,
    taskId: number,
    payload: NeoTaskPayload
  ) => {
    try {
      const data = await repo.updateProjectTaskAPI(projectId, taskId, payload);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const deleteProjectTask = async (projectId: number, taskId: number) => {
    try {
      await repo.deleteProjectTaskAPI(projectId, taskId);
      return { success: true, data: null };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const runProjectTask = async (projectId: number, taskId: number) => {
    try {
      const data = await repo.runProjectTaskAPI(projectId, taskId);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  return {
    listProjects,
    createProject,
    listTemplates,
    listPromptSuggestions,
    cloneTemplate,
    updateProject,
    deleteProject,
    getProjectShare,
    setProjectShareClinics,
    getAiCareSnfSettings,
    setAiCareSnfEnabled,
    getProjectOwners,
    setProjectOwners,
    listProjectActivity,
    listProjectAgents,
    listProjectFiles,
    listProjectWorkflowDocuments,
    listAgentFiles,
    addProjectFile,
    addAgentFile,
    patchAgentFile,
    deleteProjectFile,
    deleteAgentFile,
    listProjectInstructions,
    listAgentInstructions,
    createAgentInstruction,
    updateAgentInstruction,
    deleteAgentInstruction,
    listProjectMemories,
    listAgentMemories,
    deleteAgentMemory,
    listProjectTasks,
    createProjectTask,
    updateProjectTask,
    deleteProjectTask,
    runProjectTask,
  };
};
